import { z } from 'zod';

// One diagnosed condition of the referred patient, expressed as a standard
// FHIR (HL7) coding: `system` is a code-system URI
// (e.g. http://hl7.org/fhir/sid/icd-9-cm), `code` is the condition code
// within that system and `name` is its human-readable display text.
// Every part is trimmed while parsing: diagnosis text arrives padded from
// partner exports, and untrimmed values break the code lookups and the
// deduplication the platform does on `system|code`. Trimming before the
// emptiness check also means a whitespace-only value is rejected as the blank
// it is rather than stored.
export const ReferralDiagnosisConditionSchema = z.object({
  system: z
    .string('Condition system is required')
    .trim()
    .min(1, 'Condition system is required'),
  code: z
    .string('Condition code is required')
    .trim()
    .min(1, 'Condition code is required'),
  name: z
    .string('Condition name is required')
    .trim()
    .min(1, 'Condition name is required'),
});

// Clinical diagnosis of the referred patient as provided by the referring
// organization: `conditions` are coded, `symptoms` are the free text the
// referrer wrote. A referrer may send either or both, but a diagnosis
// carrying neither is rejected rather than stored empty.
export const ReferralDiagnosisSchema = z
  .object({
    conditions: z
      .array(ReferralDiagnosisConditionSchema)
      .min(1, 'Diagnosis conditions cannot be empty')
      .optional(),
    symptoms: z
      .array(z.string().trim().min(1, 'Diagnosis symptom cannot be empty'))
      .min(1, 'Diagnosis symptoms cannot be empty')
      .optional(),
  })
  .refine(
    (diagnosis) => !!diagnosis.conditions || !!diagnosis.symptoms,
    'Diagnosis requires conditions or symptoms',
  )
  .optional();

export type ReferralDiagnosisCondition = z.infer<
  typeof ReferralDiagnosisConditionSchema
>;

export type ReferralDiagnosis = NonNullable<
  z.infer<typeof ReferralDiagnosisSchema>
>;
